import GameService from "./game-service";
import ContextService from "./context-service";
import ValidationError from "./exceptions/validation-error";
import ValidationType from "./exceptions/validation-type";
import FootballState from "../sports/football/state/football-state";
import {
    getFieldDirectionMultiplier,
    getPeriodStartSeconds,
    secondHalfPeriodNumber
} from "../sports/football/utils/context-utils";
import DriveAlreadyCreated from "../sports/football/exceptions/drive-already-created";
import TeamService from "./team-service";
import DriveService from "./drive-service";
import DriveSwitchPossession from "../sports/football/exceptions/drive-need-possession-switch";
import DriveNotStarted from "../sports/football/exceptions/drive-not-started";
import { applicationRepository } from "../storage/storage";
import FootballStringifier from "./stringifiers/football-stringifier";
import DuplicateKickoff from "../sports/football/exceptions/duplicate-kickoff";
import DuplicatePat from "../sports/football/exceptions/duplicate-pat";
import DuplicateDriveStart from "../sports/football/exceptions/duplicate-drive-start";
import DuplicateTouchdown from "../sports/football/exceptions/duplicate-touchdown";
import InvalidPossessionChange from "../sports/football/exceptions/invalid-possession-change";
import KickoffIntoActiveDrive from "../sports/football/exceptions/kickoff-into-active-drive";
import { KICKOFF_DRIVE_END_TYPES } from "../sports/football/enums/drive-end-type";
import InvalidDriveLessInsert from "../sports/football/exceptions/invalid-driveless-insert";
import PlayInsertionType from "../sports/football/state/PlayInsertionType";
import PlayRequiresState from "../sports/football/exceptions/play-requires-state";

const BIG_LOSS_OF_YARDS = -20;

class ValidationService {
    //TODO: I cannot call ApplicationService due to circular dep. See how to fix this.
    async getAllowEventSettingsEditing() {
        const appConfig = await applicationRepository.getAll().first();

        return appConfig?.["allowEventSettingsEditing"];
    }

    async canEditEventSettings(eventId) {
        const game = await GameService.getGameByEventId(eventId);
        const gameContext = await ContextService.getGameContext(game.id);
        const allowEventSettingsEditing =
            await this.getAllowEventSettingsEditing();

        if (gameContext.isGameInitialized() && !allowEventSettingsEditing) {
            throw new ValidationError(
                "Cannot change event settings after game has started.",
                ValidationType.Info
            );
        }
    }

    async canSetClockTime(game, seconds, period) {
        const lastPlay = game.getLastPlay();
        if (lastPlay) {
            const lastPlayTimeMillis = lastPlay.time;
            const lastPeriod = lastPlay.period;

            if (period < lastPeriod) {
                throw new ValidationError(
                    "Cannot set a period backwards",
                    ValidationType.Error
                );
            }

            if (period === lastPeriod && seconds * 1000 > lastPlayTimeMillis) {
                throw new ValidationError(
                    `Cannot set clock time before last play time: ${lastPlayTimeMillis}`,
                    ValidationType.Error
                );
            }
        }

        return true;
    }

    /**
     * Validate an operator-supplied drive end time. The clock counts down, so
     * within the drive's start period the end time must be at or after (i.e. a
     * clock value at or below) the drive's start, and the end period cannot
     * precede the start period. Time must fall inside the period.
     * @param {Drive} drive
     * @param {number} seconds
     * @param {number} period
     * @param {number} minutesByPeriod
     */
    async canSetDriveEndTime(drive, seconds, period, minutesByPeriod) {
        if (!drive) {
            throw new ValidationError("Drive not found", ValidationType.Error);
        }

        const periodSeconds = getPeriodStartSeconds(minutesByPeriod);
        if (seconds < 0 || seconds > periodSeconds) {
            throw new ValidationError(
                `Drive end time must be between 0 and ${periodSeconds} seconds`,
                ValidationType.Error
            );
        }

        if (period < drive.startPeriod) {
            throw new ValidationError(
                "Drive cannot end before the period it started",
                ValidationType.Error
            );
        }

        if (period === drive.startPeriod && seconds * 1000 > drive.startTime) {
            throw new ValidationError(
                "Drive end time cannot be before its start time",
                ValidationType.Error
            );
        }

        return true;
    }

    validatePlay(play, gameContext, eventConfiguration) {
        const fieldDirectionMultiplier =
            getFieldDirectionMultiplier(gameContext);
        return {
            messages: [
                ...this.validateTimeout(play, gameContext, eventConfiguration),
                ...this.validateBigLossOfYardage(
                    play,
                    fieldDirectionMultiplier,
                    gameContext
                ),
                ...this.validateNonPositiveSack(play, fieldDirectionMultiplier)
            ]
        };
    }

    /**
     * Check that an un-finalized play can be inserted, based upon to-be-created statLogs and the rangePlays that the play will be inserted into.
     * @param {FootballPlay} newPlay
     * @param {FootballPlay | null} originalPlay
     * @param {FootballPlay[]} rangePlays
     * @param {FootballStats[]} statLogs
     * @param {boolean} isInvalidPossessionChange
     * @param {PlayInsertionType} insertType
     * @returns {Promise<FootballPlay | void>} The play, on successful validation.
     */
    async validateFootballPlayInsertion(
        newPlay,
        originalPlay,
        rangePlays,
        statLogs,
        isInvalidPossessionChange,
        insertType
    ) {
        if (insertType === PlayInsertionType.Replace) {
            const requiredState = originalPlay.getRequiredReplacementState();
            if (!newPlay.historyContains(requiredState)) {
                throw new PlayRequiresState(requiredState);
            }
        }

        // Exclude plays at the replacement index AND the new play itself when replacing
        const playsForDuplicateCheck =
            insertType === PlayInsertionType.Replace
                ? rangePlays.filter(
                      (p) => p.index !== newPlay.index && p.id !== newPlay.id
                  )
                : rangePlays;

        const isKickoff = newPlay.historyContains(FootballState.KickOff);
        // Skip duplicate kickoff check when replacing - the play being replaced may be the kickoff
        if (insertType !== PlayInsertionType.Replace) {
            const rangeHasKickoff = playsForDuplicateCheck.some((p) =>
                p.historyContains(FootballState.KickOff)
            );
            if (isKickoff && rangeHasKickoff) {
                throw new DuplicateKickoff();
            }
        }

        this._validateKickoffIntoActiveDrive(newPlay);

        const isPat = newPlay.historyContains(FootballState.PatTry);
        const rangeHasPat = playsForDuplicateCheck.some((p) =>
            p.historyContains(FootballState.PatTry)
        );
        if (isPat && rangeHasPat) {
            throw new DuplicatePat();
        }

        const isDriveStart = newPlay.historyContains(FootballState.StartDrive);
        const rangeDriveStart = playsForDuplicateCheck.find((p) =>
            p.historyContains(FootballState.StartDrive)
        );
        if (isDriveStart && rangeDriveStart) {
            const isDuplicateDrive =
                newPlay.contextSnapshot.homeTeamHasPossession ===
                rangeDriveStart.contextSnapshot.homeTeamHasPossession;
            if (isDuplicateDrive) {
                throw new DuplicateDriveStart();
            }
        }

        const isTouchdown = statLogs.some((s) => !!s.values?.touchdowns);
        if (isTouchdown) {
            const allowedStates = [
                FootballState.PatTry,
                FootballState.UpdateContext,
                FootballState.SetTimeAndPeriod,
                FootballState.PlayPenalty
            ];
            const futurePlays = rangePlays.filter(
                (p) => p.index >= newPlay.index
            );
            futurePlays.forEach((p) => {
                const hasValidState = allowedStates.some(
                    (s) =>
                        p.historyContains(s) ||
                        (isKickoff &&
                            p.historyContains(FootballState.StartDrive))
                );
                if (!hasValidState) {
                    throw new DuplicateTouchdown();
                }
            });
        }

        const noDriveExceptionStates = [
            FootballState.KickOff,
            FootballState.StartDrive,
            FootballState.PatTry
        ];
        const isInvalidDriveLessInsert =
            !noDriveExceptionStates.some((s) => newPlay.historyContains(s)) &&
            !newPlay.contextSnapshot.currentDriveId;

        if (isInvalidDriveLessInsert) {
            throw new InvalidDriveLessInsert();
        }

        if (isInvalidPossessionChange) {
            throw new InvalidPossessionChange();
        }

        return newPlay;
    }

    async validateFootball(play) {
        await this._validateDrive(play);
    }

    async _validateDrive(play) {
        if (play.isComplete()) {
            const gameContext = await ContextService.getGameContext(
                play.gameId
            );
            if (play.historyContains(FootballState.StartDrive)) {
                const driveFragment = play.getFirstFragment(
                    FootballState.StartDrive
                );
                await this._validateDriveCreation(gameContext, driveFragment);
            } else {
                await this._validateAddToDrive(play, gameContext);
            }
        }
    }

    async _validateDriveCreation(gameContext, driveFragment) {
        const secondHalfPrdNumber = secondHalfPeriodNumber(gameContext);
        const {
            gameConfig,
            triggerKO,
            gameId,
            kickingOffTeamId,
            firstQtrKickoffTeamId,
            startSettings,
            homeTeamHasPossession
        } = gameContext;
        if (
            gameContext.getCurrentPeriod() !== driveFragment.startTime.period &&
            (driveFragment.startTime.period === secondHalfPrdNumber ||
                driveFragment.startTime.period >
                    gameConfig.general.periodsInRegulation ||
                triggerKO)
        ) {
            return;
        }

        if (
            triggerKO &&
            gameContext.getCurrentPeriod() === driveFragment.startTime.period
        ) {
            const game = await GameService.getGame(gameId);
            const { contextSnapshot } = game.getLastPlay();
            if (!contextSnapshot?.driveContext?.triggerDrive) {
                throw new Error(
                    "Kickoff must be completed before starting new drive in same period!"
                );
            }
        }

        const openDrive = await DriveService.getCurrentDrive(gameId);
        if (openDrive) {
            throw new DriveAlreadyCreated();
        }

        const lastDrive = await DriveService.getLastDrive(gameId);
        if (lastDrive) {
            if (
                // First drive of second half
                driveFragment.startTime.period === secondHalfPrdNumber &&
                lastDrive.startPeriod < secondHalfPrdNumber
            ) {
                if (
                    gameContext.getOpponent(startSettings.offenseTeam.id) !==
                        gameContext.getPossessionTeamId() &&
                    (!kickingOffTeamId ||
                        kickingOffTeamId === firstQtrKickoffTeamId)
                ) {
                    const team = await TeamService.getTeam(
                        gameContext.getOpponent(startSettings.offenseTeam.id)
                    );
                    throw new DriveSwitchPossession(
                        `${team.teamName} should start with the possession into 2nd half.`
                    );
                }
            } else if (
                // First drive of OT
                gameContext.getOvertimePeriod() > 0 &&
                startSettings.offenseTeam.id !==
                    gameContext.getPossessionTeamId() &&
                lastDrive.startTime.period <=
                    gameConfig.general.periodsInRegulation
            ) {
                const team = await TeamService.getTeam(
                    startSettings.offenseTeam.id
                );
                throw new DriveSwitchPossession(
                    `${team.teamName} should start with the possession into a new OT.`
                );
            } else if (
                !(await this._isTurnoverSafetyDrive(gameId)) &&
                !(await this._isPuntExceptionDrive(gameId)) &&
                !lastDrive.scoringTeamId &&
                homeTeamHasPossession === lastDrive.homeTeamHasPossession &&
                (!kickingOffTeamId ||
                    (kickingOffTeamId !== gameContext.getPossessionTeamId() &&
                        gameContext.getOvertimePeriod() < 1))
            ) {
                const team = await TeamService.getTeam(
                    gameContext.getPossessionTeamId()
                );
                throw new DriveSwitchPossession(
                    `${team.teamName} has possession into the last drive.`
                );
            } else if (
                lastDrive.scoringTeamId &&
                lastDrive.scoringTeamId === gameContext.getPossessionTeamId() &&
                (!kickingOffTeamId ||
                    kickingOffTeamId === lastDrive.scoringTeamId) &&
                !(await this._isOnSideKickRecoveredSequence(gameId))
            ) {
                const team = await TeamService.getTeam(lastDrive.scoringTeamId);

                throw new DriveSwitchPossession(
                    `${team.teamName} scored into the last drive.`
                );
            }
        }
    }

    async _isPuntExceptionDrive(gameId) {
        const lastDriveEndingPlay = await DriveService.getLastDriveEndingPlay(
            gameId
        );
        return lastDriveEndingPlay
            ? (lastDriveEndingPlay.historyContains(FootballState.Muffed) &&
                  lastDriveEndingPlay.historyContains(
                      FootballState.FumbleRecovered
                  )) ||
                  (lastDriveEndingPlay.historyContains(
                      FootballState.Returned
                  ) &&
                      lastDriveEndingPlay.historyContains(
                          FootballState.FumbleNotRecovered
                      ))
            : false;
    }

    async _isTurnoverSafetyDrive(gameId) {
        const lastDriveEndingPlay = await DriveService.getLastDriveEndingPlay(
            gameId
        );
        return lastDriveEndingPlay
            ? lastDriveEndingPlay.historyContains(FootballState.Safety) &&
                  (lastDriveEndingPlay.historyContains(
                      FootballState.PassIntercepted
                  ) ||
                      lastDriveEndingPlay.historyContains(
                          FootballState.FumbleNotRecovered
                      ))
            : false;
    }

    _validateKickoffIntoActiveDrive(play) {
        const { currentDriveId, triggerKO, driveContext } =
            play.contextSnapshot;
        const isKickoffIntoActiveDrive =
            currentDriveId &&
            triggerKO &&
            play.historyContains(FootballState.KickOff) &&
            !KICKOFF_DRIVE_END_TYPES.includes(
                driveContext.previousDriveEndType?.code
            );

        if (isKickoffIntoActiveDrive) {
            throw new KickoffIntoActiveDrive();
        }
    }

    async _validateAddToDrive(play, gameContext) {
        const driveId = await DriveService.resolveDriveId(play, gameContext);
        const kickCoinOrPenaltyOrUpdateField =
            play.historyContains(FootballState.KickOff) ||
            play.historyContains(FootballState.PlayPenalty) ||
            play.historyContains(FootballState.CoinToss) ||
            play.historyContains(FootballState.UpdateContext);
        if (!driveId && !kickCoinOrPenaltyOrUpdateField) {
            throw new DriveNotStarted();
        }

        this._validateKickoffIntoActiveDrive(play);
    }

    validateTimeout(play, gameContext, eventConfiguration) {
        const messages = [];

        if (play.historyContains(FootballState.Timeout)) {
            const { team } = play.getFirstFragment(FootballState.Timeout);
            const currentPeriod = gameContext.getCurrentPeriod();
            const halfNumber = gameContext.halfNumber(currentPeriod);
            if (!halfNumber) {
                messages.push({
                    type: "error",
                    message: "Timeouts are not available during overtime."
                });
            } else if (
                gameContext.getTimeoutsByHalf(team.id, halfNumber) >=
                eventConfiguration.general.timeoutsPerHalf
            ) {
                messages.push({
                    type: "error",
                    message: `This team has already used all ${eventConfiguration.general.timeoutsPerHalf} of its allowed timeouts.`
                });
            }
        }

        return messages;
    }

    validateBigLossOfYardage(play, fieldDirectionMultiplier, gameContext) {
        const messages = [];

        const { summaryContext } = FootballStringifier.generateSummary(
            play,
            gameContext
        );
        const { homeTeamHasPossession } = play.contextSnapshot;
        const possessionChanged =
            homeTeamHasPossession !== summaryContext.homeTeamHasPossession;
        const yardsGained = play.getYardsGained(fieldDirectionMultiplier);

        if (!possessionChanged && yardsGained <= BIG_LOSS_OF_YARDS) {
            messages.push({
                type: "warning",
                message: `This play will result in a big loss of ${yardsGained} yards. Please double-check the yard line where the play ended.`
            });
        }

        return messages;
    }

    validateNonPositiveSack(play, fieldDirectionMultiplier) {
        const messages = [];

        const isNonPositiveSack =
            play.historyContains(FootballState.PassSacked) &&
            !play.historyContains(FootballState.PenaltyAccepted);
        if (isNonPositiveSack) {
            const yardsGained = play.getYardsGained(fieldDirectionMultiplier);
            if (yardsGained > 0) {
                messages.push({
                    type: "warning",
                    message: `This is a sack with a gain of ${yardsGained} yards. A sack should always be negative yardage. Please double-check the yard line where the play ended.`
                });
            }
        }

        return messages;
    }

    async _isOnSideKickRecoveredSequence(gameId) {
        const game = await GameService.getGame(gameId);
        const secondToLast = game.plays?.at(-2);
        return (
            secondToLast.historyContains(FootballState.OnSide) &&
            secondToLast.historyContains(FootballState.KickRecovered)
        );
    }
}

export default new ValidationService();
